/**
 * useSMSDocumentsConfig.js
 *
 * Hook for managing SMS Documents configuration - external folder reference
 * and WBC3 document checklist.
 *
 * This is a SIMPLIFIED approach:
 * - Users link to their external document folder (SharePoint, Google Drive, etc.)
 * - Simple checklist to track which WBC3 documents they have
 * - No PDF upload/storage (that's handled by the SMS Document Portal for Premium users)
 *
 * Storage Key: sms_documents
 * Pattern: Single config object per vessel (not a collection)
 *
 * ANCHOR MODE compliant - Domain Spine v2
 */

import { useCallback, useMemo, useEffect } from 'react';
import { useStorageState } from '../../useStorageState';
import { useVessel } from '../../../contexts/VesselContext';
import { usePermissions } from '../../usePermissions';
import { DOC_TYPES } from '../../../config/docRequirements';

// Storage keys
const STORAGE_KEY = 'sms_documents';
const LEGACY_KEY = 'sr.policies.v1';

// Folder type options (cloud storage only - local paths don't work in browsers)
export const FOLDER_TYPES = {
  SHAREPOINT: 'sharepoint',
  GOOGLE_DRIVE: 'googledrive',
  ONEDRIVE: 'onedrive',
  DROPBOX: 'dropbox',
  ICLOUD: 'icloud',
  OTHER: 'other'
};

// Folder type labels for UI
export const FOLDER_TYPE_LABELS = {
  sharepoint: 'SharePoint',
  googledrive: 'Google Drive',
  onedrive: 'OneDrive',
  dropbox: 'Dropbox',
  icloud: 'iCloud Drive',
  other: 'Other Cloud Storage'
};

// Folder type placeholder URLs
export const FOLDER_TYPE_PLACEHOLDERS = {
  sharepoint: 'https://company.sharepoint.com/sites/SMS/Documents',
  googledrive: 'https://drive.google.com/drive/folders/abc123...',
  onedrive: 'https://1drv.ms/f/s!AbCdEfGh...',
  dropbox: 'https://www.dropbox.com/scl/fo/abc123/...',
  icloud: 'https://www.icloud.com/iclouddrive/abc123...',
  other: 'https://your-cloud-storage.com/sms-documents'
};

// Help text for each type
export const FOLDER_TYPE_HELP = {
  sharepoint: 'Navigate to your SMS folder in SharePoint, then copy the URL from your browser.',
  googledrive: 'Right-click the folder → Share → Copy link. Make sure sharing is set to "Anyone with link".',
  onedrive: 'Right-click the folder → Share → Copy link.',
  dropbox: 'Right-click the folder → Share → Create link → Copy link.',
  icloud: 'Go to iCloud.com, navigate to folder, copy URL from browser.',
  other: 'Paste the web URL to your cloud-hosted SMS documents folder.'
};

// RBAC helper (same pattern as other config hooks)
const canDo = (can, resource, action) => {
  try {
    if (!can || typeof can !== 'function') return false;
    return (
      (can.length >= 2 && can(resource, action)) ||
      can(`${resource}:${action}`) ||
      can({ resource, action }) ||
      can(action, resource)
    );
  } catch {
    return false;
  }
};

/**
 * Generate initial checklist from DOC_TYPES
 * Creates an entry for each document type with default values
 */
const generateInitialChecklist = () => {
  const checklist = {};
  Object.values(DOC_TYPES).forEach(docType => {
    checklist[docType] = {
      hasDocument: false,
      notes: '',
      lastUpdated: null,
      updatedBy: null
    };
  });
  return checklist;
};

/**
 * Default configuration structure
 */
const getDefaultConfig = () => ({
  version: 1,
  revision: 1,

  // Folder location
  folderUrl: '',
  folderType: FOLDER_TYPES.OTHER,

  // Document checklist - one entry per DOC_TYPES
  documentChecklist: generateInitialChecklist(),

  // Metadata
  lastModified: null,
  modifiedBy: { name: '', userId: '' }
});

/**
 * Check for legacy data and migrate if found
 */
const checkLegacyData = (vesselId) => {
  if (!vesselId) return null;

  try {
    const legacyKey = `${LEGACY_KEY}_${vesselId}`;
    const legacyData = localStorage.getItem(legacyKey);

    if (legacyData) {
      const parsed = JSON.parse(legacyData);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn('[useSMSDocumentsConfig] Error reading legacy data:', e);
  }

  return null;
};

/**
 * Migrate legacy documents to checklist format
 */
const migrateFromLegacy = (legacyDocs, currentUser) => {
  const checklist = generateInitialChecklist();
  const timestamp = new Date().toISOString();
  const user = { name: currentUser?.name || 'Migration', userId: currentUser?.id || 'system' };

  legacyDocs.forEach(doc => {
    if (doc.type && checklist[doc.type]) {
      checklist[doc.type] = {
        hasDocument: true,
        notes: `Migrated from legacy storage. Original: ${doc.title || doc.type}`,
        lastUpdated: timestamp,
        updatedBy: user
      };
    }
  });

  return checklist;
};

/**
 * SMS Documents Configuration Hook
 *
 * @returns {Object} Config state and operations
 */
export function useSMSDocumentsConfig() {
  const { currentVesselId } = useVessel();
  const { can, currentUser } = usePermissions();

  // Use vessel-scoped storage
  const { value: rawConfig, setValue: setConfig } = useStorageState(
    STORAGE_KEY,
    getDefaultConfig()
  );

  // Check for and migrate legacy data on first load
  useEffect(() => {
    if (!currentVesselId || !rawConfig) return;

    // Only migrate if config is fresh (no folder URL set and no checklist items checked)
    const hasExistingConfig = rawConfig.folderUrl ||
      Object.values(rawConfig.documentChecklist || {}).some(item => item.hasDocument);

    if (!hasExistingConfig) {
      const legacyDocs = checkLegacyData(currentVesselId);
      if (legacyDocs && legacyDocs.length > 0) {
        console.log('[useSMSDocumentsConfig] Found legacy data, migrating...');
        const migratedChecklist = migrateFromLegacy(legacyDocs, currentUser);

        setConfig({
          ...getDefaultConfig(),
          documentChecklist: migratedChecklist,
          revision: 1,
          lastModified: new Date().toISOString(),
          modifiedBy: { name: 'Migration', userId: 'system' }
        });
      }
    }
  }, [currentVesselId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ensure config has all required fields (handles upgrades)
  const config = useMemo(() => {
    if (!rawConfig) return getDefaultConfig();

    const defaultConfig = getDefaultConfig();
    return {
      ...defaultConfig,
      ...rawConfig,
      // Ensure checklist has all document types
      documentChecklist: {
        ...defaultConfig.documentChecklist,
        ...(rawConfig.documentChecklist || {})
      }
    };
  }, [rawConfig]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  /**
   * Count of confirmed documents
   */
  const confirmedCount = useMemo(() => {
    return Object.values(config.documentChecklist || {})
      .filter(item => item.hasDocument).length;
  }, [config.documentChecklist]);

  /**
   * Total required documents
   */
  const totalCount = useMemo(() => {
    return Object.keys(config.documentChecklist || {}).length;
  }, [config.documentChecklist]);

  /**
   * Compliance percentage
   */
  const compliancePercentage = useMemo(() => {
    if (totalCount === 0) return 0;
    return Math.round((confirmedCount / totalCount) * 100);
  }, [confirmedCount, totalCount]);

  /**
   * Check if folder is configured
   */
  const hasFolderConfigured = useMemo(() => {
    return Boolean(config.folderUrl && config.folderUrl.trim());
  }, [config.folderUrl]);

  // ============================================================================
  // MUTATIONS
  // ============================================================================

  /**
   * Update folder location
   */
  const setFolderLocation = useCallback(async (url, type = FOLDER_TYPES.OTHER) => {
    if (!canDo(can, 'policies', 'update')) {
      console.warn('[useSMSDocumentsConfig] Permission denied');
      return false;
    }

    const updatedConfig = {
      ...config,
      revision: (config.revision || 0) + 1,
      folderUrl: url,
      folderType: type,
      lastModified: new Date().toISOString(),
      modifiedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    await setConfig(updatedConfig);
    return true;
  }, [can, config, setConfig, currentUser]);

  /**
   * Toggle document checkbox
   */
  const toggleDocument = useCallback(async (docType, hasDocument) => {
    if (!canDo(can, 'policies', 'update')) {
      console.warn('[useSMSDocumentsConfig] Permission denied');
      return false;
    }

    const timestamp = new Date().toISOString();
    const user = { name: currentUser?.name, userId: currentUser?.id };

    const updatedChecklist = {
      ...config.documentChecklist,
      [docType]: {
        ...config.documentChecklist[docType],
        hasDocument,
        lastUpdated: timestamp,
        updatedBy: user
      }
    };

    const updatedConfig = {
      ...config,
      revision: (config.revision || 0) + 1,
      documentChecklist: updatedChecklist,
      lastModified: timestamp,
      modifiedBy: user
    };

    await setConfig(updatedConfig);
    return true;
  }, [can, config, setConfig, currentUser]);

  /**
   * Update document notes
   */
  const setDocumentNotes = useCallback(async (docType, notes) => {
    if (!canDo(can, 'policies', 'update')) {
      console.warn('[useSMSDocumentsConfig] Permission denied');
      return false;
    }

    const timestamp = new Date().toISOString();
    const user = { name: currentUser?.name, userId: currentUser?.id };

    const updatedChecklist = {
      ...config.documentChecklist,
      [docType]: {
        ...config.documentChecklist[docType],
        notes,
        lastUpdated: timestamp,
        updatedBy: user
      }
    };

    const updatedConfig = {
      ...config,
      revision: (config.revision || 0) + 1,
      documentChecklist: updatedChecklist,
      lastModified: timestamp,
      modifiedBy: user
    };

    await setConfig(updatedConfig);
    return true;
  }, [can, config, setConfig, currentUser]);

  /**
   * Reset to defaults
   */
  const resetToDefaults = useCallback(async () => {
    if (!canDo(can, 'policies', 'update')) {
      console.warn('[useSMSDocumentsConfig] Permission denied');
      return false;
    }

    const defaults = getDefaultConfig();
    defaults.revision = (config.revision || 0) + 1;
    defaults.lastModified = new Date().toISOString();
    defaults.modifiedBy = { name: currentUser?.name, userId: currentUser?.id };

    await setConfig(defaults);
    return true;
  }, [can, config, setConfig, currentUser]);

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // Raw config
    config,

    // Computed values
    confirmedCount,
    totalCount,
    compliancePercentage,
    hasFolderConfigured,

    // Direct settings access
    folderUrl: config.folderUrl,
    folderType: config.folderType,
    documentChecklist: config.documentChecklist,

    // Mutations
    setFolderLocation,
    toggleDocument,
    setDocumentNotes,
    resetToDefaults,

    // Metadata
    lastModified: config.lastModified,
    modifiedBy: config.modifiedBy,
    revision: config.revision,

    // Constants for UI
    FOLDER_TYPES,
    FOLDER_TYPE_LABELS,
    FOLDER_TYPE_PLACEHOLDERS,
    FOLDER_TYPE_HELP
  };
}

export default useSMSDocumentsConfig;
